<template>
    <ClientOnly v-if="isEditable">
        <MenuBar v-if="editor" :editor="editor" @edit-mode-active="toggleEditMode" @content-saved="save" @content-published="publish" />

        <div :class="isEditMode ? 'editor__frame' : ''" class="content_styles">
            <EditorContent :editor="editor" />
        </div>
    </ClientOnly>
    <div v-else class="content_styles" v-html="content"></div>
</template>

<script setup lang="ts">
    import {onBeforeUnmount, onMounted, ref} from 'vue';
    import {Image} from '@tiptap/extension-image';
    import {Link} from '@tiptap/extension-link';
    import {Table} from '@tiptap/extension-table';
    import {TableCell} from '@tiptap/extension-table-cell';
    import {TableHeader} from '@tiptap/extension-table-header';
    import {TableRow} from '@tiptap/extension-table-row';
    import {TextAlign} from '@tiptap/extension-text-align';
    import {Underline} from '@tiptap/extension-underline';
    import {StarterKit} from '@tiptap/starter-kit';
    import {Editor, EditorContent} from '@tiptap/vue-3';
    import {useLocale, useUniversalPost} from '#imports';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';
    import MenuBar from './MenuBar.vue';

    const props = defineProps({
        content: {
            type: String,
            required: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        humanId: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
    });

    const isEditable = !props.readonly;
    const isEditMode = ref(false);

    const editor = ref<Editor | undefined>();
    const {locale} = useLocale();

    function getEditorOptions() {
        return {
            editable: false,
            content: props.content,
            extensions: [
                Image,
                Table,
                TableRow,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                TableHeader,
                TableCell,
                Underline,
                Link.configure({
                    openOnClick: false,
                }),
                StarterKit.configure({
                    heading: {
                        levels: [1, 2, 3, 4],
                    },
                }),
            ],
        };
    }

    function createEditor() {
        if (props.readonly) {
            return;
        }
        if (editor.value) {
            return;
        }
        editor.value = new Editor(getEditorOptions());
    }

    function destroyEditor() {
        editor.value?.destroy();
        editor.value = undefined;
    }

    async function save() {
        try {
            await useUniversalPost('/api/content', {
                query: {
                    humanId: props.humanId,
                    type: props.type,
                    locale: locale.value,
                },
                body: editor.value?.getHTML(),
            });
            pushVJoySnackbar({props: {message: 'Content saved!'}});
        } catch (_) {
            pushVJoySnackbar({props: {message: 'Content could not be saved!', level: 'error'}});
        }
    }

    async function publish() {
        try {
            await useUniversalPost('/api/content/versions', {
                query: {
                    humanId: props.humanId,
                    type: props.type,
                    locale: locale.value,
                },
                body: editor.value?.getHTML(),
            });
            pushVJoySnackbar({props: {message: 'Content published!'}});
        } catch (_) {
            pushVJoySnackbar({props: {message: 'Content could not be published!', level: 'error'}});
        }
    }

    function toggleEditMode(editMode: boolean) {
        isEditMode.value = editMode;
        editor.value?.setEditable(editMode);
    }

    onBeforeUnmount(destroyEditor);

    onMounted(() => {
        createEditor();
    });
</script>

<style lang="scss">
    .content_styles {
        h2,
        h3,
        h4 {
            margin-top: var(--joy-core-spacing-8);
            margin-bottom: var(--joy-core-spacing-4);
        }
        h2 {
            font-size: var(--joy-font-size-primary-800);
            color: var(--joy-color-secondary-90);
            font-family: var(--joy-font-weight-bold);
        }

        h3 {
            font-size: var(--joy-font-size-primary-600);
            font-family: var(--joy-font-weight-bold);
            color: var(--joy-color-primary-90);
            margin-top: var(--joy-core-spacing-8);
        }

        h4 {
            margin-bottom: 20px;
            font-size: var(--joy-font-size-primary-400);
            font-family: var(--joy-font-weight-bold);
            margin-bottom: var(--joy-core-spacing-3);
        }
        table {
            width: 100%;
            table-layout: fixed;

            td,
            th {
                border: 1px solid var(--joy-color-neutral-40);
                padding: 10px;
                word-break: break-word;
            }

            th {
                font-weight: var(--joy-font-weight-bold);
                background-color: var(--joy-color-neutral-30);
            }
        }
        a {
            color: var(--joy-color-secondary-30);
            text-decoration: none;
            font-weight: var(--joy-font-weight-bold);
            &:hover {
                text-decoration: underline;
                color: var(--joy-color-secondary-90);
            }
        }
    }

    .editor {
        background-color: var(--joy-color-neutral-0);
        border: 3px solid var(--joy-color-neutral-70);
        border-radius: var(--joy-core-radius-3);
        color: var(--joy-color-text-title);
        display: flex;
        flex-direction: column;
        max-height: 26rem;

        &__frame {
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);
            padding: var(--joy-core-spacing-2);
        }
    }
</style>
